import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';

import type { Errors } from '#src/components/so5/Rules/formatRules.ts';
import { getErrorFrom } from '#src/components/so5/Rules/formatRules.ts';
import type { FormatRule } from '#src/components/so5/Rules/types.ts';

import { ComposeRule } from './ComposeRule/index.tsx';
import type {
  ComposeRules_displayedTypedRule,
  ComposeRules_so5RuleFeedback,
} from './__generated__/index.graphql.ts';

const Icon = styled.img`
  object-fit: contain;
`;

type Props = {
  displayedTypedRules: ComposeRules_displayedTypedRule[];
  scoringMatrixRules?: ReactNode;
  minified?: boolean;
  errors?: Errors;
  rulesFeedbacks?: ComposeRules_so5RuleFeedback[];
};

export const ComposeRules = ({
  displayedTypedRules,
  scoringMatrixRules,
  minified,
  errors = [],
  rulesFeedbacks = [],
}: Props) => {
  const normalizedRules: FormatRule[] = displayedTypedRules.flatMap(
    ({ __typename, key, descriptions }) => {
      const ruleFeedback = rulesFeedbacks.find(
        ({ ruleName }) => ruleName === key
      );

      return (
        descriptions?.map((description, i) => ({
          id: `${__typename}-${i}`,
          icon: description.icon && (
            <Icon src={description.icon} alt="" width={16} height={16} />
          ),
          label: description.title,
          description: (
            <Vertical as="span" gap={0}>
              {description.items.map((item, j) => (
                // eslint-disable-next-line react/no-array-index-key
                <Horizontal key={j} as="span">
                  {!!item.icon && (
                    <Icon src={item.icon} alt="" width={16} height={16} />
                  )}
                  {item.title}
                </Horizontal>
              ))}
            </Vertical>
          ),
          validationState: {
            message:
              getErrorFrom(errors, key) || ruleFeedback?.message || undefined,
            state: ruleFeedback?.state,
          },
        })) || []
      );
    }
  );

  return (
    <Vertical gap={1.5} className={classNames({ minified })}>
      {normalizedRules.map(rule => (
        <ComposeRule key={rule.id} rule={rule} minified={minified} />
      ))}
      {scoringMatrixRules}
    </Vertical>
  );
};

ComposeRules.fragments = {
  displayedTypedRule: gql`
    fragment ComposeRules_displayedTypedRule on So5RuleInterface {
      id
      key
      descriptions {
        icon
        title
        items {
          icon
          title
        }
      }
    }
  ` as TypedDocumentNode<ComposeRules_displayedTypedRule>,
  so5RuleFeedback: gql`
    fragment ComposeRules_so5RuleFeedback on So5RuleFeedback {
      ruleName
      state
      message
    }
  ` as TypedDocumentNode<ComposeRules_so5RuleFeedback>,
};
